import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Send, TestTube, Users, AlertCircle } from 'lucide-react';
import emailCampaignService from '../../services/emailCampaignService';

const EmailCampaignForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template: '',
    filter_criteria: {},
    status: 'draft',
  });

  const [templates, setTemplates] = useState([]);
  const [filterPreview, setFilterPreview] = useState(null);
  const [availableFilters, setAvailableFilters] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [showTestModal, setShowTestModal] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, [id]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [templatesData, filtersData] = await Promise.all([
        emailCampaignService.getTemplates({ is_active: true }),
        emailCampaignService.getAvailableFilters()
      ]);

      setTemplates(Array.isArray(templatesData) ? templatesData : templatesData.results || []);
      setAvailableFilters(filtersData);

      if (isEdit) {
        const campaignData = await emailCampaignService.getCampaign(id);
        setFormData({
          name: campaignData.name,
          description: campaignData.description || '',
          template: campaignData.template,
          filter_criteria: campaignData.filter_criteria || {},
          status: campaignData.status,
        });
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterKey, value) => {
    setFormData({
      ...formData,
      filter_criteria: {
        ...formData.filter_criteria,
        [filterKey]: value
      }
    });
  };

  const handlePreviewFilter = async () => {
    try {
      const preview = await emailCampaignService.previewFilter(formData.filter_criteria);
      setFilterPreview(preview);
    } catch (err) {
      console.error('Filter preview failed:', err);
      alert('Failed to preview filter');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (isEdit) {
        await emailCampaignService.updateCampaign(id, formData);
      } else {
        const newCampaign = await emailCampaignService.createCampaign(formData);
        navigate(`/admin/email-campaigns/campaigns/${newCampaign.id}`);
        return;
      }

      alert('Campaign saved successfully');
    } catch (err) {
      console.error('Save failed:', err);
      setError(err.response?.data?.error || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handlePrepare = async () => {
    if (!id) {
      alert('Please save the campaign first');
      return;
    }

    try {
      setSaving(true);
      const result = await emailCampaignService.prepareCampaign(id);
      alert(`Campaign prepared with ${result.recipient_count} recipients`);
      window.location.reload();
    } catch (err) {
      console.error('Prepare failed:', err);
      alert(err.response?.data?.error || 'Failed to prepare campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      alert('Please enter a test email address');
      return;
    }

    try {
      setSaving(true);
      await emailCampaignService.sendTestEmail(id, testEmail);
      alert(`Test email sent to ${testEmail}`);
      setShowTestModal(false);
      setTestEmail('');
    } catch (err) {
      console.error('Test email failed:', err);
      alert(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'Edit Campaign' : 'Create New Campaign'}
          </h1>
          <p className="mt-2 text-gray-600">
            {isEdit ? 'Update campaign details and filters' : 'Set up a new email campaign'}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Campaign Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Welcome Email Campaign"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Campaign description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Template *
                </label>
                <select
                  required
                  value={formData.template}
                  onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select a template</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name} ({template.category})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* User Filters */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">User Filters</h2>
              <button
                onClick={handlePreviewFilter}
                className="btn-secondary text-sm inline-flex items-center space-x-2"
              >
                <Users className="h-4 w-4" />
                <span>Preview Recipients</span>
              </button>
            </div>

            <div className="space-y-4">
              {/* Signup Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Signup Status
                </label>
                <select
                  value={formData.filter_criteria.signup_status || 'all'}
                  onChange={(e) => handleFilterChange('signup_status', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="all">All Users</option>
                  <option value="verified">Verified Only</option>
                  <option value="unverified">Unverified Only</option>
                </select>
              </div>

              {/* Credit Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Credit Balance Range
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="number"
                    placeholder="Min credits"
                    value={formData.filter_criteria.credit_range?.min || ''}
                    onChange={(e) => handleFilterChange('credit_range', {
                      ...formData.filter_criteria.credit_range,
                      min: parseInt(e.target.value) || 0
                    })}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <input
                    type="number"
                    placeholder="Max credits"
                    value={formData.filter_criteria.credit_range?.max || ''}
                    onChange={(e) => handleFilterChange('credit_range', {
                      ...formData.filter_criteria.credit_range,
                      max: parseInt(e.target.value) || 999999
                    })}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Activity Filters */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Active Users (logged in within days)
                  </label>
                  <input
                    type="number"
                    placeholder="e.g., 7"
                    value={formData.filter_criteria.last_login_days || ''}
                    onChange={(e) => handleFilterChange('last_login_days', parseInt(e.target.value) || null)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Inactive Users (not logged in for days)
                  </label>
                  <input
                    type="number"
                    placeholder="e.g., 30"
                    value={formData.filter_criteria.inactive_days || ''}
                    onChange={(e) => handleFilterChange('inactive_days', parseInt(e.target.value) || null)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Carousel Filters */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Has Created Carousel
                </label>
                <select
                  value={formData.filter_criteria.has_created_carousel === undefined ? '' : formData.filter_criteria.has_created_carousel.toString()}
                  onChange={(e) => handleFilterChange('has_created_carousel', e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Any</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              {/* Paid User Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Type
                </label>
                <select
                  value={formData.filter_criteria.is_paid_user === undefined ? '' : formData.filter_criteria.is_paid_user.toString()}
                  onChange={(e) => handleFilterChange('is_paid_user', e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">All Users</option>
                  <option value="true">Paid Users Only</option>
                  <option value="false">Free Users Only</option>
                </select>
              </div>
            </div>

            {/* Filter Preview */}
            {filterPreview && (
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-blue-900">Filter Preview</h3>
                  <span className="text-2xl font-bold text-blue-600">
                    {filterPreview.total_count} users
                  </span>
                </div>
                {filterPreview.sample_users && filterPreview.sample_users.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm text-blue-800 mb-2">Sample users:</p>
                    <div className="space-y-1">
                      {filterPreview.sample_users.map(user => (
                        <div key={user.id} className="text-sm text-blue-700">
                          {user.email} {user.first_name && `(${user.first_name} ${user.last_name})`}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="bg-white p-6 rounded-lg shadow-md">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleSave}
                disabled={saving || !formData.name || !formData.template}
                className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-5 w-5" />
                <span>{saving ? 'Saving...' : 'Save Campaign'}</span>
              </button>

              {isEdit && (
                <>
                  <button
                    onClick={handlePrepare}
                    disabled={saving}
                    className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Users className="h-5 w-5" />
                    <span>Prepare Recipients</span>
                  </button>

                  <button
                    onClick={() => setShowTestModal(true)}
                    disabled={saving}
                    className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50"
                  >
                    <TestTube className="h-5 w-5" />
                    <span>Send Test Email</span>
                  </button>
                </>
              )}

              <button
                onClick={() => navigate('/admin/email-campaigns/campaigns')}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Test Email Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Send Test Email</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Test Email Address
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="admin@example.com"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleSendTest}
                disabled={saving || !testEmail}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Sending...' : 'Send Test'}
              </button>
              <button
                onClick={() => {
                  setShowTestModal(false);
                  setTestEmail('');
                }}
                disabled={saving}
                className="flex-1 btn-secondary disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailCampaignForm;
